/**
 * Settings → Users (design-reference §4.12).
 *
 * The tenant's users (GET /v1/users) with invite (POST /v1/users), change-role
 * and deactivate/reactivate (PATCH / DELETE /v1/users/{id}). Status is derived
 * from real fields — a user with no linked identity is shown as "Invited". A
 * last-admin guard refuses removing the final active tenant admin (the backend
 * enforces it too; a backend refusal surfaces the same message).
 */
import { useCallback, useMemo, useState } from 'react';

import type { User, UserRole } from '../../api/schema';
import { Button, ErrorState, Modal, Skeleton, useToast } from '../../components';
import { useSession } from '../../auth/session';
import { useLoadState } from '../../lib/useLoadState';
import { useSettingsApi } from './settingsApi';
import { fmtDate, initials, roleLabel } from './format';
import './settings.css';

const INVITE_ROLES: readonly { value: UserRole; label: string }[] = [
  { value: 'tenant_admin', label: 'Tenant admin' },
  { value: 'tenant_analyst', label: 'Analyst' },
  { value: 'tenant_viewer', label: 'Viewer' },
];

/**
 * A user's display status, derived from real fields (no server "invited" state).
 *
 * "Invited" means no external identity has been linked at all yet. Under
 * AUTH_MODE=entra, `entra_oid` is set only once the person actually completes
 * an Entra sign-in, so this is precise there. Under AUTH_MODE=auth0,
 * `auth0_sub` is set as soon as the account is provisioned — before the
 * invite email is even sent — so a user shows "Active" from invite time, not
 * from their first real sign-in; Auth0 doesn't push us a signal for that
 * distinction today. Still strictly better than the alternative this replaced,
 * which showed "Invited" forever, even for a user who had long since signed in.
 */
function statusView(user: User): { label: string; dot: 'ok' | 'warn' | 'idle' } {
  const hasIdentity = user.entra_oid != null || user.auth0_sub != null;
  if (!hasIdentity && user.status === 'active') return { label: 'Invited', dot: 'warn' };
  if (user.status === 'active') return { label: 'Active', dot: 'ok' };
  return { label: 'Deactivated', dot: 'idle' };
}

function isActiveAdmin(user: User): boolean {
  return user.role === 'tenant_admin' && user.status === 'active';
}

export function UsersScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();
  const { principal } = useSession();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('tenant_analyst');
  const [inviting, setInviting] = useState(false);

  const [roleTarget, setRoleTarget] = useState<User | null>(null);
  const [roleValue, setRoleValue] = useState<UserRole>('tenant_analyst');
  const [roleBusy, setRoleBusy] = useState(false);

  const fetchUsers = useCallback(
    async (signal: AbortSignal): Promise<User[]> => {
      const page = await settingsApi.users.list(signal);
      return page.items;
    },
    [settingsApi],
  );
  const state = useLoadState(fetchUsers);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  const adminCount = useMemo(
    () => (state.status === 'ready' ? state.data.filter(isActiveAdmin).length : 0),
    [state],
  );

  const submitInvite = useCallback(() => {
    if (inviteEmail.trim() === '' || inviteName.trim() === '') return;
    setInviting(true);
    void (async () => {
      try {
        await settingsApi.users.invite({
          email: inviteEmail.trim(),
          display_name: inviteName.trim(),
          role: inviteRole,
        });
        toast('Invite sent — expires in 7 days');
        setInviteOpen(false);
        setInviteName('');
        setInviteEmail('');
        setInviteRole('tenant_analyst');
        reload?.();
      } catch {
        toast('Could not send the invite — check the email and try again', { tone: 'error' });
      } finally {
        setInviting(false);
      }
    })();
  }, [inviteEmail, inviteName, inviteRole, toast, reload, settingsApi]);

  const deactivate = useCallback(
    (user: User) => {
      // Last-admin / self-lockout guard, mirrored from the backend.
      if (isActiveAdmin(user) && adminCount <= 1) {
        toast("You can't deactivate the last admin — invite another admin first");
        return;
      }
      void (async () => {
        try {
          await settingsApi.users.deactivate(user.id);
          toast(`${user.display_name} deactivated — sessions revoked`);
          reload?.();
        } catch {
          toast("You can't deactivate the last admin — invite another admin first", { tone: 'error' });
        }
      })();
    },
    [adminCount, toast, reload, settingsApi],
  );

  const reactivate = useCallback(
    (user: User) => {
      void (async () => {
        try {
          await settingsApi.users.update(user.id, { status: 'active' });
          toast(`${user.display_name} reactivated`);
          reload?.();
        } catch {
          toast('Could not reactivate the user — try again', { tone: 'error' });
        }
      })();
    },
    [toast, reload, settingsApi],
  );

  const submitRole = useCallback(() => {
    const user = roleTarget;
    if (user === null) return;
    // Last-admin guard, mirrored from the backend: demoting the only active admin
    // out of the admin role would lock the tenant out. Parity with deactivate().
    if (isActiveAdmin(user) && roleValue !== 'tenant_admin' && adminCount <= 1) {
      toast("You can't demote the last admin — promote another admin first");
      return;
    }
    setRoleBusy(true);
    void (async () => {
      try {
        await settingsApi.users.update(user.id, { role: roleValue });
        toast(`${user.display_name} is now ${roleLabel(roleValue)}`);
        setRoleTarget(null);
        reload?.();
      } catch {
        toast('Could not change the role — try again', { tone: 'error' });
      } finally {
        setRoleBusy(false);
      }
    })();
  }, [roleTarget, roleValue, adminCount, toast, reload, settingsApi]);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Users">
        <Skeleton height={320} />
        <p className="set-loading-caption">Loading users…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Users">
        <ErrorState
          title="We couldn't load your users"
          body="The settings service didn't respond. Try again in a moment."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const users = state.data;

  return (
    <section className="set-wrap" aria-label="Users">
      <div className="set-card">
        <div className="set-card__head">
          <h2 className="set-card__title">Users</h2>
          <span className="set-card__aside">
            {users.length} seat{users.length === 1 ? '' : 's'} in use
          </span>
          <span className="set-card__spacer" />
          <Button variant="primary" size="sm" onClick={() => { setInviteOpen(true); }}>
            + Invite user
          </Button>
        </div>

        <div className="set-scroll">
          <div
            className="set-grid set-grid__head"
            role="row"
            style={{ gridTemplateColumns: '1.6fr 1.6fr 1fr 0.9fr 0.9fr 110px' }}
          >
            <span role="columnheader">Name</span>
            <span role="columnheader">Email</span>
            <span role="columnheader">Role</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Added</span>
            <span role="columnheader" className="sr-only">
              Actions
            </span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Tenant users">
            {users.map((user) => {
              const view = statusView(user);
              const isYou = principal?.user_id === user.id;
              const deactivated = user.status !== 'active';
              return (
                <li
                  className="set-grid set-grid__row"
                  key={user.id}
                  style={{ gridTemplateColumns: '1.6fr 1.6fr 1fr 0.9fr 0.9fr 110px' }}
                >
                  <span className="set-user">
                    <span className="set-avatar" aria-hidden="true">
                      {initials(user.display_name)}
                    </span>
                    <span style={{ fontWeight: 500 }}>
                      {user.display_name}
                      {isYou ? ' (you)' : ''}
                    </span>
                  </span>
                  <span className="set-mono" style={{ fontSize: 11.5, color: 'var(--t2)' }}>
                    {user.email}
                  </span>
                  <span>
                    <button type="button" className="set-linkbtn" onClick={() => {
                      setRoleTarget(user);
                      setRoleValue(user.role);
                    }}>
                      {roleLabel(user.role)}
                    </button>
                  </span>
                  <span className="set-status">
                    <span className={`set-dot set-dot--${view.dot}`} aria-hidden="true" />
                    {view.label}
                  </span>
                  <span style={{ color: 'var(--t3)' }}>{fmtDate(user.created_at)}</span>
                  <span style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="set-linkbtn"
                      onClick={() => {
                        if (deactivated) reactivate(user);
                        else deactivate(user);
                      }}
                    >
                      {deactivated ? 'Reactivate' : 'Deactivate'}
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <p className="set-card__foot">
          <strong style={{ color: 'var(--t2)' }}>Roles.</strong> Tenant admin — everything incl. settings &amp;
          taxonomy · Analyst — read + review queue · Viewer — read-only dashboards &amp; chatbot.
        </p>
      </div>

      {/* Invite modal ------------------------------------------------------- */}
      <Modal
        open={inviteOpen}
        onClose={() => { setInviteOpen(false); }}
        title="Invite a user"
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button variant="secondary" onClick={() => { setInviteOpen(false); }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={inviting}
              disabled={inviteEmail.trim() === '' || inviteName.trim() === ''}
              onClick={submitInvite}
            >
              Send invite
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <div className="ds-field">
            <label className="ds-field__label" htmlFor="invite-name">
              Name
            </label>
            <input
              id="invite-name"
              className="ds-input"
              placeholder="Full name"
              value={inviteName}
              onChange={(e) => { setInviteName(e.currentTarget.value); }}
            />
          </div>
          <div className="ds-field" style={{ marginTop: 12 }}>
            <label className="ds-field__label" htmlFor="invite-email">
              Work email
            </label>
            <input
              id="invite-email"
              className="ds-input"
              type="email"
              placeholder="name@acme.com"
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.currentTarget.value); }}
            />
          </div>
          <fieldset style={{ border: 'none', margin: '12px 0 0', padding: 0 }}>
            <legend className="ds-field__label">Role</legend>
            <div className="set-seg" role="group" aria-label="Role">
              {INVITE_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  className={r.value === inviteRole ? 'set-seg__btn set-seg__btn--on' : 'set-seg__btn'}
                  aria-pressed={r.value === inviteRole}
                  onClick={() => { setInviteRole(r.value); }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
      </Modal>

      {/* Change-role modal -------------------------------------------------- */}
      <Modal
        open={roleTarget !== null}
        onClose={() => { setRoleTarget(null); }}
        title={`Change role — ${roleTarget?.display_name ?? ''}`}
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button variant="secondary" onClick={() => { setRoleTarget(null); }}>
              Cancel
            </Button>
            <Button variant="primary" loading={roleBusy} onClick={submitRole}>
              Save role
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend className="ds-field__label">Role</legend>
            <div className="set-seg" role="group" aria-label="New role">
              {INVITE_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  className={r.value === roleValue ? 'set-seg__btn set-seg__btn--on' : 'set-seg__btn'}
                  aria-pressed={r.value === roleValue}
                  onClick={() => { setRoleValue(r.value); }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
      </Modal>
    </section>
  );
}
